import { Button as AntButton, Modal } from 'antd';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createCoupon,
  createCouponWithAmount,
  getStudentCouponTypes,
} from '../../../../api/coupons2.api';
import useAxios from '../../../../hooks/useAxios';
import useNotification from '../../../../hooks/useNotification';
import { IGetStudentCouponTypes } from '../../../../models/coupons2.model';
import { IStudent } from '../../../../models/students.model';
import ErrorResponse from '../../../ErrorResponse';
import Loader from '../../../Loader';
import Radio from '../../../Radio';

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  student: IStudent | undefined;
  getStudentCoupons: (memberCode: string) => void;
}

const IssueCouponModal: FC<IProps> = ({
  visible,
  setVisible,
  student,
  getStudentCoupons,
}) => {
  const { t } = useTranslation();
  const { infoNotification, successNotification } = useNotification();
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetStudentCouponTypes>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();

  const [couponCode, setCouponCode] = useState<number>();
  const [memo, setMemo] = useState('');
  const [amount, setAmount] = useState('');
  const [showAmountInput, setShowAmountInput] = useState(false);

  useEffect(() => {
    if (student) {
      execute(getStudentCouponTypes(student.campusCode, student.studentCode));
    }
  }, [student]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      if (student) getStudentCoupons(student.studentCode);
      successNotification(t('student.student.message40'));
      onClose();
    }
  }, [submitRes, submitIsLoading]);

  const onSubmit = () => {
    if (!student) return;
    if (!couponCode) {
      return infoNotification(t('student.student.message41'));
    }
    if (showAmountInput && !amount) {
      return infoNotification(t('student.student.message42'));
    }
    const regex = /^[0-9]+$/;
    if (showAmountInput && !regex.test(amount)) {
      return infoNotification(t('student.student.message44'));
    }

    showAmountInput
      ? submitExecute(
          createCouponWithAmount(couponCode, student.studentCode, {
            amount: Number(amount),
            memo,
          }),
        )
      : submitExecute(createCoupon(couponCode, student.studentCode, { memo }));
  };

  const onClose = () => {
    setCouponCode(undefined);
    setShowAmountInput(false);
    setAmount('');
    setMemo('');
    setVisible(false);
  };

  return (
    <>
      {isLoading || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />

      <Modal
        title={t('student.student.button19')}
        visible={visible}
        onCancel={onClose}
        width={600}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton key="submit" className='btn-basic pink' onClick={onSubmit}>
              {t('student.student.button20')}
            </AntButton>
            <AntButton key="cancel" className='btn-basic gray' onClick={onClose}>
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <div className='comm-table__area line-type mt-0'>
          <table>
            <thead>
              <tr>
                <th className="text-center">{t('student.student.column99')}</th>
                <th className="text-center">{t('student.student.column100')}</th>
                <th className="text-center">{t('student.student.column101')}</th>
                <th className="text-center">{t('student.student.column102')}</th>
              </tr>
            </thead>
            <tbody>
              {res?.data?.map((item) => (
                <tr key={item.couponCode}>
                  <td className="text-center">{item.couponName}</td>
                  <td className="text-center">
                    {item.amount ? item.amount.toLocaleString() : '-'}
                  </td>
                  <td className="text-center">
                    {moment(item.createEndDate).format('YYYY.MM.DD')}
                  </td>
                  <td className="text-center">
                    <Radio
                      checked={item.couponCode === couponCode}
                      onChange={() => {
                        setCouponCode(item.couponCode);
                        setAmount('');
                        if (item.amount) setShowAmountInput(false);
                        else setShowAmountInput(true);
                      }}
                      text=""
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {showAmountInput ? (
            <div className="flex flex-justify-center mb-2">
              <div className="mx-2 v-line">{t('student.student.column97')}</div>
              <input
                type="text"
                className="form-control input-text w-240p"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') onSubmit();
                }}
              />
            </div>
          ) : null}
          <div className="comm-bx__space">
            <div className='comm-btn__area justify-content-center'>
              <div className="mx-2 v-line">{t('common.text.note')}</div>
              <input
                type="text"
                className="form-control input-text w-240p"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') onSubmit();
                }}
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default IssueCouponModal;
